import React, { useCallback, useEffect, useState } from "react";
import "./AdminPlanEntitlements.css";
import { AUTH_BASE_URL, ensureArray } from "./config";
import { getAuthToken } from "./auth/authUtils";

export default function AdminPlanEntitlements() {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [signalKey, setSignalKey] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [definitions, setDefinitions] = useState({ signals: [], groups: [] });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { Authorization: `Bearer ${getAuthToken()}` };
      const [response, definitionsResponse] = await Promise.all([
        fetch(`${AUTH_BASE_URL}/auth/admin/plans/entitlements`, { headers, cache: "no-store" }),
        fetch(`${AUTH_BASE_URL}/auth/admin/plans/entitlement-definitions`, { headers, cache: "no-store" }),
      ]);
      if (!response.ok) throw new Error(`Unable to load plan entitlements (${response.status})`);
      if (!definitionsResponse.ok) throw new Error(`Unable to load approved signal keys (${definitionsResponse.status})`);
      const data = ensureArray(await response.json());
      const available = await definitionsResponse.json();
      setPlans(data);
      setDefinitions({ signals: ensureArray(available.signals), groups: ensureArray(available.groups) });
      setSelectedPlan((current) => current || data[0]?.planCode || "");
    } catch (exception) {
      setError(exception.message || "Unable to load plan entitlements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const updateAccess = async (type, key, active) => {
    const normalized = key;
    if (!selectedPlan || !normalized) return;
    const operationKey = `${type}:${normalized}`;
    setSavingKey(operationKey);
    setError("");
    try {
      const response = await fetch(
        `${AUTH_BASE_URL}/auth/admin/plans/${encodeURIComponent(selectedPlan)}/${type}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ key: normalized, active }),
        }
      );
      if (!response.ok) throw new Error((await response.text()) || `Update failed (${response.status})`);
      await loadPlans();
    } catch (exception) {
      setError(exception.message || "Unable to update entitlement");
    } finally {
      setSavingKey("");
    }
  };

  const plan = plans.find((item) => item.planCode === selectedPlan);
  const renderEntries = (entries, type) => (
    <div className="entitlement-list">
      {ensureArray(entries).length === 0 && <p className="entitlement-empty">No mappings configured.</p>}
      {ensureArray(entries).map((entry) => {
        const operationKey = `${type}:${entry.key}`;
        return (
          <label className="entitlement-entry" key={entry.key}>
            <input
              type="checkbox"
              checked={Boolean(entry.active)}
              disabled={Boolean(savingKey)}
              onChange={(event) => updateAccess(type, entry.key, event.target.checked)}
            />
            <span>{entry.key}</span>
            {savingKey === operationKey && <small>Saving…</small>}
          </label>
        );
      })}
    </div>
  );

  return (
    <section className="admin-section plan-entitlements">
      <div className="plan-entitlements__heading">
        <div>
          <h2>Plan Signal Entitlements</h2>
          <p>Control the V2 signal groups and individual signals available to each subscription plan.</p>
        </div>
        <button type="button" onClick={loadPlans} disabled={loading || Boolean(savingKey)}>Refresh</button>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {loading ? <p className="admin-loading">Loading plan entitlements…</p> : (
        <>
          <label className="plan-entitlements__selector">
            <span>Subscription plan</span>
            <select value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)}>
              {plans.map((item) => <option key={item.planId} value={item.planCode}>{item.planCode}</option>)}
            </select>
          </label>

          {plan && (
            <div className="plan-entitlements__grid">
              <EntitlementColumn
                title="Signal groups"
                placeholder="VOLUME_SIGNALS"
                options={definitions.groups}
                value={groupKey}
                setValue={setGroupKey}
                add={() => { updateAccess("signal-groups", groupKey, true); setGroupKey(""); }}
                disabled={Boolean(savingKey)}
              >{renderEntries(plan.signalGroups, "signal-groups")}</EntitlementColumn>
              <EntitlementColumn
                title="Individual signals"
                placeholder="VOLUME_BREAKOUT"
                options={definitions.signals}
                value={signalKey}
                setValue={setSignalKey}
                add={() => { updateAccess("signals", signalKey, true); setSignalKey(""); }}
                disabled={Boolean(savingKey)}
              >{renderEntries(plan.signals, "signals")}</EntitlementColumn>
            </div>
          )}
          {plan && !plan.mappingsConfigured && (
            <p className="entitlement-compatibility">{plan.planCode === "PREMIUM" ? "No mappings exist. PREMIUM retains access to all premium signals." : "No mappings exist. BASIC receives free signals only until an entitlement is added."}</p>
          )}
        </>
      )}
    </section>
  );
}

function EntitlementColumn({ title, placeholder, options, value, setValue, add, disabled, children }) {
  return (
    <div className="plan-entitlements__column">
      <h3>{title}</h3>
      <div className="entitlement-add">
        <select value={value} aria-label={title} onChange={(event) => setValue(event.target.value)}>
          <option value="">Select {placeholder}</option>
          {ensureArray(options).map(option => <option key={option.key} value={option.key}>{option.displayName} ({option.key})</option>)}
        </select>
        <button type="button" disabled={disabled || !value.trim()} onClick={add}>Add</button>
      </div>
      {children}
    </div>
  );
}
